import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import type { MailboxProviderId } from 'fym-shared/src/music24/outreach/mailboxProviders';
import { TypedMemo } from '../../../common/types';
import { MAILBOX_GUIDE_COPY } from './mailboxGuideCopy';
import { isGoogleWorkspace, providerOf } from './mailboxGuideState';
import { mailboxGuideMessages as messages } from './mailboxGuideMessages';
import { ProviderMark } from './ProviderMark';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the guide; each part uses its own slice of it */
import styles from './MailboxGuide.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface DetectionBannerProps {
  /** A detected provider that Music24 supports (never `other`, never an unsupported one). */
  providerId: MailboxProviderId;
  email: string;
  /** "Not Gmail?": the detection is wrong, the person picks a tile. */
  onDismiss: () => void;
}

/** The green banner after the lookup (design outreach-v2 p2 `.det`): what we found and what comes next. */
const DetectionBannerComponent: React.FC<DetectionBannerProps> = ({ providerId, email, onDismiss }) => {
  const intl = useIntl();
  const workspace = isGoogleWorkspace(providerId, email);
  const provider = intl.formatMessage(workspace ? messages.foundWorkspace : MAILBOX_GUIDE_COPY[providerId].label);
  const next =
    providerOf(providerId).passwordKind === 'appPassword' ? messages.foundAppPassword : messages.foundEmailPassword;
  return (
    <div className={styles.detected} data-testid="mailbox-detected">
      <span className={styles.tick} aria-hidden="true">
        <FontAwesomeIcon icon={faCheck} />
      </span>
      <ProviderMark providerId={providerId} />
      <div className={styles.detectedText}>
        <b>{intl.formatMessage(messages.foundTitle, { provider })}</b>
        <small className="text-body-secondary">{intl.formatMessage(next)}</small>
      </div>
      <button type="button" className={styles.linkButton} onClick={onDismiss}>
        {intl.formatMessage(messages.foundNot, { provider })}
      </button>
    </div>
  );
};

export const DetectionBanner = TypedMemo(DetectionBannerComponent);
